import React, { useContext, useState, useMemo } from 'react';

import { Button } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import Emoji from 'react-emoji-render';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faLayerGroup } from '@fortawesome/free-solid-svg-icons';

import { AuthContext } from 'components/providers/AuthContextProvider';
import { DASHBOARD_WIDGETS } from 'lib/constants';
import DashboardWidgetTile from 'components/dashboard/DashboardWidgetTile';
import DashboardWidgetType, {
  DashboardWidgetTypeLabels,
  WidgetClickHandlers,
} from 'lib/enums/DashboardWidgetTypes';
import ThumbnailAssigningStats from 'components/manage_brands/ThumbnailAssigningStats';
import useLocalStorage from 'lib/hooks/useLocalStorage';
import WidgetSelectionModal from 'components/dashboard/WidgetSelectionModal';
import {
  backgroundGrey5,
  white,
  squidInk,
  coolGrey,
  blackberry,
  grape,
  purple1,
  greyStroke,
  shadowMedium,
} from 'lib/css/colors';

function DashboardView() {
  const { authUser } = useContext(AuthContext);
  const [showModal, setShowModal] = useState(false);
  const [activeWidgets, setActiveWidgets] = useLocalStorage(DASHBOARD_WIDGETS, [
    DashboardWidgetType.VERIFIED_VENDOR_PRODUCTS,
    DashboardWidgetType.VERIFIED_BRANDS,
    DashboardWidgetType.VERIFIED_MANUFACTURERS,
    DashboardWidgetType.TAGGED_IMAGES,
    DashboardWidgetType.VERIFICATION_PENDING_BRANDS,
    DashboardWidgetType.VERIFICATION_PENDING_MANUFACTURERS,
    DashboardWidgetType.VERIFIED_SIMILAR_BRANDS,
    DashboardWidgetType.CONTENT_VERIFIED_MANUFACTURER_PRODUCTS,
  ]);
  const widgetOptions = useMemo(
    () => [
      {
        category: 'Default Widgets',
        options: Object.values(DashboardWidgetType).map(widgetType => ({
          value: widgetType,
          label: DashboardWidgetTypeLabels[widgetType],
        })),
      },
      {
        category: 'Thumbnail Assigning Stats',
        options: [
          {
            value: 'THUMBNAIL_ASSIGNING_STATS',
            label: 'Thumbnail Assigning Stats',
          },
        ],
      },
    ],
    [],
  );

  const activeDefaultWidgets = activeWidgets.filter(w =>
    Object.values(DashboardWidgetType).includes(w),
  );

  const firstName = authUser.name ? authUser.name.split(' ')[0] : 'there';

  return (
    <div className={css(styles.pageContainer)}>
      {/* Header Section */}
      <div className={css(styles.headerSection)}>
        <div className={css(styles.headerContent)}>
          <div className={css(styles.greetingContainer)}>
            <h1 className={css(styles.greeting)}>
              Welcome back, {firstName}
              <Emoji text=" 👋" className={css(styles.wave)} />
            </h1>
            <p className={css(styles.subGreeting)}>
              Here&apos;s what&apos;s happening with your data today
            </p>
          </div>
          <Button onClick={() => setShowModal(true)} className={css(styles.addButton)}>
            <FontAwesomeIcon icon={faPlus} className={css(styles.buttonIcon)} />
            Customize Dashboard
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className={css(styles.mainContent)}>
        {/* Metrics Overview Section */}
        {activeDefaultWidgets.length > 0 && (
          <div className={css(styles.section)}>
            <div className={css(styles.sectionHeader)}>
              <FontAwesomeIcon icon={faLayerGroup} className={css(styles.sectionIcon)} />
              <h2 className={css(styles.sectionTitle)}>Metrics Overview</h2>
              <span className={css(styles.widgetCount)}>{activeDefaultWidgets.length} widgets</span>
            </div>
            <div className={css(styles.gridContainer)}>
              {activeWidgets.map(
                widgetType =>
                  Object.values(DashboardWidgetType).includes(widgetType) && (
                    <div key={widgetType} className={css(styles.widgetWrapper)}>
                      <DashboardWidgetTile
                        widgetType={widgetType}
                        label={DashboardWidgetTypeLabels[widgetType]}
                        onClick={WidgetClickHandlers[widgetType]}
                      />
                    </div>
                  ),
              )}
            </div>
          </div>
        )}

        {/* Thumbnail Stats Section */}
        {activeWidgets.includes('THUMBNAIL_ASSIGNING_STATS') && (
          <div className={css(styles.section)}>
            <ThumbnailAssigningStats />
          </div>
        )}

        {/* Empty State */}
        {activeWidgets.length === 0 && (
          <div className={css(styles.emptyState)}>
            <div className={css(styles.emptyStateIcon)}>
              <FontAwesomeIcon icon={faLayerGroup} size="3x" />
            </div>
            <h3 className={css(styles.emptyStateTitle)}>No widgets selected</h3>
            <p className={css(styles.emptyStateText)}>
              Click &quot;Customize Dashboard&quot; to add widgets and start tracking your metrics.
            </p>
            <Button onClick={() => setShowModal(true)} className={css(styles.emptyStateButton)}>
              <FontAwesomeIcon icon={faPlus} className={css(styles.buttonIcon)} />
              Add Widgets
            </Button>
          </div>
        )}
      </div>

      {/* Widget Selection Modal */}
      {showModal && (
        <WidgetSelectionModal
          show={showModal}
          onHide={() => setShowModal(false)}
          widgetOptions={widgetOptions}
          activeWidgets={activeWidgets}
          setActiveWidgets={setActiveWidgets}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: backgroundGrey5,
  },
  headerSection: {
    background: `linear-gradient(135deg, ${blackberry} 0%, ${purple1} 100%)`,
    padding: '32px 40px',
    marginBottom: 0,
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: 1600,
    margin: '0 auto',
  },
  greetingContainer: {
    color: white,
  },
  greeting: {
    fontSize: 28,
    fontWeight: 600,
    margin: 0,
    color: white,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  subGreeting: {
    fontSize: 15,
    opacity: 0.9,
    marginTop: 6,
    marginBottom: 0,
    color: white,
  },
  wave: {
    display: 'inline-block',
    animationName: {
      '0%': { transform: 'rotate(0deg)' },
      '10%': { transform: 'rotate(14deg)' },
      '20%': { transform: 'rotate(-8deg)' },
      '30%': { transform: 'rotate(14deg)' },
      '40%': { transform: 'rotate(-4deg)' },
      '50%': { transform: 'rotate(10deg)' },
      '60%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(0deg)' },
    },
    animationDuration: '2s',
    animationIterationCount: 'infinite',
    transformOrigin: '70% 70%',
  },
  addButton: {
    backgroundColor: white,
    color: blackberry,
    border: 'none',
    borderRadius: 8,
    padding: '12px 20px',
    fontWeight: 600,
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    transition: 'all 0.2s ease',
    boxShadow: `0 2px 8px ${shadowMedium}`,
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 4px 12px ${shadowMedium}`,
      backgroundColor: grape,
      color: white,
    },
  },
  buttonIcon: {
    fontSize: 14,
  },
  mainContent: {
    padding: '24px 40px 40px',
    maxWidth: 1600,
    margin: '0 auto',
  },
  section: {
    marginBottom: 32,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
  },
  sectionIcon: {
    color: grape,
    fontSize: 18,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: squidInk,
    margin: 0,
  },
  widgetCount: {
    backgroundColor: `${grape}25`,
    color: blackberry,
    fontSize: 12,
    fontWeight: 500,
    padding: '4px 10px',
    borderRadius: 12,
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 20,
  },
  widgetWrapper: {
    display: 'flex',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 40px',
    backgroundColor: white,
    borderRadius: 16,
    border: `2px dashed ${greyStroke}`,
    textAlign: 'center',
  },
  emptyStateIcon: {
    color: coolGrey,
    opacity: 0.5,
    marginBottom: 20,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: squidInk,
    margin: 0,
    marginBottom: 8,
  },
  emptyStateText: {
    fontSize: 15,
    color: coolGrey,
    margin: 0,
    marginBottom: 24,
    maxWidth: 400,
  },
  emptyStateButton: {
    backgroundColor: blackberry,
    color: white,
    border: 'none',
    borderRadius: 8,
    padding: '12px 24px',
    fontWeight: 600,
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: grape,
      transform: 'translateY(-2px)',
    },
  },
});

export default DashboardView;
